import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import Tour from './Tour';
import TourChapterModal from './TourChapterModal';
import { tourChapters } from './tourSteps';
import { allTemporaryData } from './tourData';
import RecipeList from './RecipeList';
import AddRecipeModal from './AddRecipeModal';
import CookbookManager from './CookbookManager';
import AddCookbookModal from './AddCookbookModal';
import CategoryManager from './CategoryManager';
import AddCategoryModal from './AddCategoryModal';
import AdditionalItemGroupManager from './AdditionalItemGroupManager';
import AddAdditionalItemGroupModal from './AddAdditionalItemGroupModal';
import MealplanManager from './MealplanManager';
import FilterModal from './FilterModal';
import ConfirmationModal from './ConfirmationModal';
import MealPlan from './MealPlan';
import EditMealplanModal from './EditMealplanModal';
import AddMealPlanModal from './AddMealPlanModal';
import * as api from './api';
import * as apiMocks from './apiMocks';
import IngredientManager from './IngredientManager';
import AddIngredientModal from './AddIngredientModal';
import MealplanIngredientsManager from './MealplanIngredientsManager';
import FindRecipesByIngredientsManager from './FindRecipesByIngredientsManager';
import FaqModal from './FaqModal';
import logoBlack from './logo_black_and_white_256x256.png';
import logoWhite from './logo_white_and_black_256x256.png';
import SettingsModal from './SettingsModal';
import DebugWindow from './DebugWindow';

import { useAuth } from './context/AuthContext';
import { useRecipes } from './hooks/useRecipes';
import { useMealPlan } from './hooks/useMealPlan';
import { useCookbooks } from './hooks/useCookbooks';
import { useCategories } from './hooks/useCategories';
import { useAdditionalItemGroups } from './hooks/useAdditionalItemGroups';
import { useIngredients } from './hooks/useIngredients';
import { useFocusOnExpand } from './hooks/useFocusOnExpand';
import { useScrollOnExpand } from './hooks/useScrollOnExpand';

/**
 * MainLayout component holds the primary UI and logic for the authenticated user experience.
 * It manages state for recipes, cookbooks, categories, ingredients, meal plans, and UI elements like modals and tours.
 */
function MainLayout() {
  const { token, user, logout, updateUser, updateTheme, selectedTheme } = useAuth();

  // --- STATE MANAGEMENT ---

  // Tour State
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isChapterModalOpen, setIsChapterModalOpen] = useState(false);
  const [activeTourSteps, setActiveTourSteps] = useState([]);

  // UI State
  const [expandedSection, setExpandedSection] = useState('recipes');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({ isOpen: false, message: '', onConfirm: () => { }, onCancel: () => { } });
  const [isBackToTopVisible, setIsBackToTopVisible] = useState(false);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAddMealPlanModalOpen, setIsAddMealPlanModalOpen] = useState(false);


  // Refs for focusing search inputs
  const categorySearchRef = useRef(null);
  const cookbookSearchRef = useRef(null);
  const recipeSearchRef = useRef(null);
  const mealplanSearchRef = useRef(null);
  const ingredientSearchRef = useRef(null);
  const additionalItemGroupSearchRef = useRef(null);

  // Refs for collapsible sections
  const categoriesSectionRef = useRef(null);
  const cookbooksSectionRef = useRef(null);
  const recipesSectionRef = useRef(null);
  const ingredientsSectionRef = useRef(null);
  const mealplansSectionRef = useRef(null);
  const additionalItemGroupsSectionRef = useRef(null);


  // Data Selection State
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [selectedIngredientIds, setSelectedIngredientIds] = useState([]);
  const [isShuffled, setIsShuffled] = useState(false);

  // --- API & CUSTOM HOOKS ---
  const activeApi = isTourOpen ? apiMocks : api;

  // Use a ref to break the dependency cycle between useMealPlan and useAdditionalItemGroups
  // useMealPlan needs the selected group IDs on save, and useAdditionalItemGroups needs functions from useMealPlan
  const selectedIdsRef = useRef();

  const {
    activeMealPlan, isMealPlanDirty, isEditMealplanModalOpen, editingMealplan, globalServingSize,
    mealPlanIngredients, shoppingList, setShoppingList, mealplanSearchTerm, mealplanPage, setMealplanPage,
    filteredMealplans, mealplans, loadMealPlan, clearActiveMealPlan, closeMealPlan, toggleRecipeInMealPlan, removeRecipeFromMealPlan, updateRecipeInMealPlan,
    reorderMealPlanRecipe, moveRecipe, updateNumberOfDays, updateServingSize, saveActiveMealPlan, addAdditionalItem, removeSourceFromAdditionalItem,
    deleteAdditionalItem, updateAdditionalItem, deleteMealplan, editMealplan, startEditingMealplan,
    cancelEditingMealplan, handleSearchChange: handleMealplanSearchChange, setIsMealPlanDirty, unselectAllMealplans, addMealPlan, updateStartDay,
  } = useMealPlan({ activeApi, token, setConfirmationModal, selectedIdsRef });

  const {
    cookbooks, filteredCookbooks, isCookbookModalOpen, setIsCookbookModalOpen, editingCookbook,
    cookbookSearchTerm, cookbookPage, setCookbookPage, selectedCookbookIds, setSelectedCookbookIds,
    addCookbook, deleteCookbook, startEditingCookbook, cancelEditingCookbook, openAddCookbookModal,
    handleCookbookSelection, unselectAllCookbooks, handleSearchChange: handleCookbookSearchChange,
  } = useCookbooks({ activeApi, token, setConfirmationModal, onCookbookDeleted: (id) => setSelectedCookbookIds(prev => prev.filter(item => (typeof item === 'object' ? item.id : item) !== id)) });

  const {
    categories, filteredCategories, isCategoryModalOpen, editingCategory, categorySearchTerm,
    categoryPage, setCategoryPage, selectedCategoryIds, setSelectedCategoryIds, addCategory,
    deleteCategory, startEditingCategory, cancelEditingCategory, openAddCategoryModal,
    handleCategorySelection, unselectAllCategories, handleSearchChange: handleCategorySearchChange,
  } = useCategories({ activeApi, token, setConfirmationModal, onCategoryDeleted: (id) => setSelectedCategoryIds(prev => prev.filter(pid => pid !== id)) });

  const {
    additionalItemGroups, filteredGroups: filteredAdditionalItemGroups, isModalOpen: isAdditionalItemGroupModalOpen,
    editingGroup: editingAdditionalItemGroup, searchTerm: additionalItemGroupSearchTerm, page: additionalItemGroupPage,
    setPage: setAdditionalItemGroupPage, selectedGroupIds: selectedAdditionalItemGroupIds,
    setSelectedGroupIds: setSelectedAdditionalItemGroupIds,
    addGroup: addAdditionalItemGroup, deleteGroup: deleteAdditionalItemGroup, startEditingGroup: startEditingAdditionalItemGroup,
    cancelEditingGroup: cancelEditingAdditionalItemGroup, openAddGroupModal: openAddAdditionalItemGroupModal,
    handleSelection: handleAdditionalItemGroupSelection, unselectAll: unselectAllAdditionalItemGroups, handleSearchChange: handleAdditionalItemGroupSearchChange,
  } = useAdditionalItemGroups({ activeApi, token, setConfirmationModal, addAdditionalItem, removeSourceFromAdditionalItem, onGroupDeleted: (id) => setSelectedAdditionalItemGroupIds(prev => prev.filter(pid => pid !== id)) });

  // Keep the ref updated with the latest selected group IDs
  useEffect(() => {
    selectedIdsRef.current = selectedAdditionalItemGroupIds;
  }, [selectedAdditionalItemGroupIds]);

  const onRecipeSaved = useCallback((savedRecipe, wasEditingId) => {
    if (wasEditingId) {
      updateRecipeInMealPlan(savedRecipe);
    }
  }, [updateRecipeInMealPlan]);

  const onRecipeDeleted = useCallback((deletedRecipeId) => {
    removeRecipeFromMealPlan(deletedRecipeId);
  }, [removeRecipeFromMealPlan]);

  const {
    ingredients, filteredIngredients, isIngredientModalOpen, setIsIngredientModalOpen, editingIngredient,
    searchTerm: ingredientSearchTerm, page: ingredientPage, setPage: setIngredientPage, addIngredient,
    deleteIngredient, startEditingIngredient, cancelEditingIngredient, openAddIngredientModal,
    handleSearchChange: handleIngredientSearchChange,
  } = useIngredients({ activeApi, token, setConfirmationModal, onIngredientDeleted: (id) => setSelectedIngredientIds(prev => prev.filter(pid => pid !== id)) });

  const {
    recipes, filteredRecipes, searchTerm, handleRecipeSearchChange, recipePage, setRecipePage,
    editingRecipeId, isRecipeModalOpen, setIsRecipeModalOpen, addRecipe, deleteRecipe,
    startEditingRecipe, openAddRecipeModal, cancelEditingRecipe,
  } = useRecipes({
    activeApi, token, ingredients, addIngredient, selectedCookbookIds, selectedCategoryIds, selectedIngredientIds, onRecipeSaved, onRecipeDeleted, setConfirmationModal
  });

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setRecipePage(1);
  }, [searchTerm, selectedCookbookIds, selectedCategoryIds, selectedIngredientIds]);

  // --- EFFECTS ---

  // Focus on search input when a section is expanded
  useFocusOnExpand(expandedSection === 'categories', categorySearchRef, isTourOpen);
  useFocusOnExpand(expandedSection === 'cookbooks', cookbookSearchRef, isTourOpen);
  useFocusOnExpand(expandedSection === 'recipes', recipeSearchRef, isTourOpen);
  useFocusOnExpand(expandedSection === 'mealplans', mealplanSearchRef, isTourOpen);
  useFocusOnExpand(expandedSection === 'ingredients', ingredientSearchRef, isTourOpen);
  useFocusOnExpand(expandedSection === 'itemGroups', additionalItemGroupSearchRef, isTourOpen);

  // Scroll to the bottom of the section when it is expanded
  useScrollOnExpand(expandedSection !== 'categories', categoriesSectionRef);
  useScrollOnExpand(expandedSection !== 'cookbooks', cookbooksSectionRef);
  useScrollOnExpand(expandedSection !== 'recipes', recipesSectionRef);
  useScrollOnExpand(expandedSection !== 'ingredients', ingredientsSectionRef);
  useScrollOnExpand(expandedSection !== 'mealplans', mealplansSectionRef);
  useScrollOnExpand(expandedSection !== 'itemGroups', additionalItemGroupsSectionRef);

  // Show/hide 'Back to Top' button on scroll
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsBackToTopVisible(true);
      } else {
        setIsBackToTopVisible(false);
      }
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  // Sync selected recipes with the active meal plan
  useEffect(() => {
    setSelectedRecipes(activeMealPlan.recipes.map(r => r.recipeId || r.id));
  }, [activeMealPlan.recipes]);

  // Sync selected item groups with the active meal plan
  useEffect(() => {
    setSelectedAdditionalItemGroupIds(activeMealPlan.selectedAdditionalItemGroupIds || []);
  }, [activeMealPlan.selectedAdditionalItemGroupIds, setSelectedAdditionalItemGroupIds]);

  // --- HANDLERS & HELPERS ---

  const handleRecipeSelection = (recipe) => {
    toggleRecipeInMealPlan(recipe);
  };

  const unselectAllRecipes = () => {
    setConfirmationModal({
      isOpen: true,
      message: 'Are you sure you want to unselect all recipes? This will remove them from your meal plan.',
      onConfirm: () => {
        selectedRecipes.forEach(recipeId => removeRecipeFromMealPlan(recipeId));
        setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { }, onCancel: () => { } });
      }
    });
  };

  const handleIngredientSelection = (ingredientId, operation = 'OR') => {
    setSelectedIngredientIds(prev => {
      const existingIndex = prev.findIndex(item => item.id === ingredientId);
      if (existingIndex >= 0) {
        // If already selected with same operation, remove it (toggle)
        if (prev[existingIndex].operation === operation) {
          return prev.filter((_, index) => index !== existingIndex);
        }
        // If operation is different, update it
        const newSelection = [...prev];
        newSelection[existingIndex] = { id: ingredientId, operation };
        return newSelection;
      }
      // If not selected, add it
      return [...prev, { id: ingredientId, operation }];
    });
  };

  const unselectAllIngredients = () => {
    setSelectedIngredientIds([]);
  };

  const handleRemoveCookbook = (cookbookId) => {
    setSelectedCookbookIds(prev => prev.filter(item => (typeof item === 'object' ? item.id : item) !== cookbookId));
  };

  const handleRemoveCategory = (categoryId) => {
    setSelectedCategoryIds(prev => prev.filter(item => (typeof item === 'object' ? item.id : item) !== categoryId));
  };

  const handleRemoveIngredient = (ingredientId) => {
    setSelectedIngredientIds(prev => prev.filter(item => (typeof item === 'object' ? item.id : item) !== ingredientId));
  };



  const handleApplyFilter = (newCookbookIds, newCategoryIds, newIngredientIds) => {
    const normalizedCookbooks = newCookbookIds.map(item => {
      if (typeof item === 'string') return { id: item, operation: 'OR' };
      return item;
    });
    setSelectedCookbookIds(normalizedCookbooks);
    // When applying from FilterModal, we receive an array of IDs or objects.
    // If they are IDs, we map them to default 'OR' operations (or keep as ID if we updated FilterModal).
    // Safest bet: check type.
    const normalizedCategories = newCategoryIds.map(item => {
      if (typeof item === 'string') return { id: item, operation: 'OR' };
      return item;
    });
    setSelectedCategoryIds(normalizedCategories);

    const normalizedIngredients = newIngredientIds.map(item => {
      if (typeof item === 'string') return { id: item, operation: 'OR' };
      return item;
    });
    setSelectedIngredientIds(normalizedIngredients);
    setIsFilterModalOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrintShoppingList = () => {
    document.body.classList.add('printing-shopping-list');
    window.print();
    document.body.classList.remove('printing-shopping-list');
  };

  const handlePrintIngredientList = () => {
    document.body.classList.add('printing-ingredient-list');
    window.print();
    document.body.classList.remove('printing-ingredient-list');
  };

  const handleToggleShoppingListItem = (ingredient) => {
    setShoppingList(prevList => {
      const key = `${ingredient.name}|${ingredient.unit}`;
      const exists = prevList.find(item => `${item.name}|${item.unit}` === key);
      if (exists) {
        return prevList.filter(item => `${item.name}|${item.unit}` !== key);
      } else {
        return [...prevList, { name: ingredient.name, unit: ingredient.unit }];
      }
    });
    setIsMealPlanDirty(true);
  };

  const handleSelectAllIngredients = () => {
    const allIngredientKeys = mealPlanIngredients.map(ingredient => ({
      name: ingredient.name,
      unit: ingredient.unit,
    }));
    setShoppingList(allIngredientKeys);
    setIsMealPlanDirty(true);
  };

  const handleClearShoppingList = () => {
    setShoppingList([]);
    setIsMealPlanDirty(true);
  };

  const handleLogout = () => {
    if (isMealPlanDirty) {
      setConfirmationModal({
        isOpen: true,
        message: 'You have unsaved changes in your meal plan. Are you sure you want to sign out? Your changes will be lost.',
        onConfirm: () => {
          activeApi.clearAutoSavedMealPlan();
          logout();
        },
      });
    } else {
      logout();
    }
  };

  // --- TOUR LOGIC ---

  const startTour = () => {
    setIsFaqModalOpen(false);
    setIsChapterModalOpen(true);
  };

  const startChapterTour = (selectedSteps, chapterTitle) => {
    const finalStep = {
      target: 'body',
      content: "That's it! You're ready to start organizing your recipes. Enjoy!",
      placement: 'center',
    };
    const stepsForTour = [...selectedSteps, finalStep];
    const tempRecipe = { ...allTemporaryData.recipe };

    setExpandedSection('recipes');
    clearActiveMealPlan(true);
    setActiveTourSteps(stepsForTour);
    setIsChapterModalOpen(false);

    setTimeout(() => {
      setIsTourOpen(true);
      if (['Meal Plan', 'Shopping List', 'Recipes'].includes(chapterTitle)) {
        handleRecipeSelection(tempRecipe);
      }
    }, 0);
  };

  const endTour = () => {
    setIsTourOpen(false);
    setActiveTourSteps([]);
    // Clean up temporary data if it was added
    // This is a simplification; ideally we'd track what was added and remove it
    clearActiveMealPlan(true);
    setIsCookbookModalOpen(false);
    setIsIngredientModalOpen(false);
    setIsRecipeModalOpen(false);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (isTourOpen) {
          endTour();
        }
      }
    };

    if (isTourOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTourOpen, endTour]);

  const tourHelpers = {
    expandCategories: () => setExpandedSection('categories'),
    collapseCategories: () => setExpandedSection(null),
    expandCookbooks: () => setExpandedSection('cookbooks'),
    collapseCookbooks: () => setExpandedSection(null),
    expandIngredients: () => setExpandedSection('ingredients'),
    collapseIngredients: () => setExpandedSection(null),
    selectFirstRecipe: () => {
      const recipeToSelect = recipes.find(r => r.isTemporary) || (filteredRecipes.length > 0 ? filteredRecipes[0] : null);
      if (recipeToSelect && !selectedRecipes.includes(recipeToSelect.id)) {
        handleRecipeSelection(recipeToSelect);
      }
    },
    selectFirstIngredient: () => {
      if (mealPlanIngredients.length > 0 && shoppingList.length === 0) {
        handleToggleShoppingListItem(mealPlanIngredients[0]);
      }
    },
    openAddCategoryModal,
    closeAddCategoryModal: cancelEditingCategory,
    openAddCookbookModal,
    closeAddCookbookModal: cancelEditingCookbook,
    openAddIngredientModal,
    closeAddIngredientModal: cancelEditingIngredient,
    openAddRecipeModal,
    closeAddRecipeModal: cancelEditingRecipe,
    openFilterModal: () => setIsFilterModalOpen(true),
    closeFilterModal: () => setIsFilterModalOpen(false),
    selectAllIngredients: handleSelectAllIngredients,
  };

  // --- RENDER ---

  if (!token) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="App-container">
      <DebugWindow />
      <header className="App-header">
        <img src={user?.theme === 'light' ? logoWhite : logoBlack} className="App-logo" alt="logo" />
        <h1>Plan your meals</h1>
        {user && (
          <div className="header-right">
            <div className="header-user-info">
              <span>Welcome, {user.firstName || user.username}</span>
              <button title="Open settings" onClick={() => setIsSettingsModalOpen(true)} className="button">
                <i className="material-icons">settings</i>
                Settings
              </button>
              <button title="Sign out" onClick={handleLogout} className="button">
                <i className="material-icons">logout</i>Sign Out
              </button>
            </div>
            <div className="hamburger-menu-icon" onClick={() => setIsMobileMenuOpen(true)}>
              <i className="material-icons">menu</i>
            </div>
          </div>
        )}
      </header>
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="mobile-menu" onClick={e => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <h2>Menu</h2>
              <button className="icon-button" onClick={() => setIsMobileMenuOpen(false)}>
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="mobile-menu-items">
              <button title="Settings" onClick={() => { setIsSettingsModalOpen(true); setIsMobileMenuOpen(false); }} className="button">
                <i className="material-icons">settings</i>
                Settings
              </button>
              <button title="Open FAQ" onClick={() => { setIsFaqModalOpen(true); setIsTourOpen(false); setIsChapterModalOpen(false); setIsMobileMenuOpen(false); }} className="button">
                <i className="material-icons">help_outline</i>
                How do I?
              </button>
              <button title="Take a tour" onClick={() => { startTour(); setIsMobileMenuOpen(false); }} className="button">
                <i className="material-icons">auto_awesome</i>
                Take a Tour
              </button>
              <button title="Sign out" onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="button">
                <i className="material-icons">logout</i>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="App">
        <Tour isOpen={isTourOpen} onClose={endTour} steps={activeTourSteps} helpers={tourHelpers} />
        <TourChapterModal isOpen={isChapterModalOpen} onClose={() => setIsChapterModalOpen(false)} chapters={tourChapters} onSelectChapter={startChapterTour} />

        <div className="left-panel">
          <div className="collapsible-section" ref={mealplansSectionRef}>
            <div className="section-header" onClick={() => setExpandedSection(expandedSection === 'mealplans' ? null : 'mealplans')}>
              <div style={{ display: 'flex', alignItems: 'center' }}><i className="material-icons" style={{ marginRight: '5px', verticalAlign: 'top' }}>event_note</i><h2>Meal plans ({filteredMealplans.length !== mealplans.length ? `${filteredMealplans.length} of ${mealplans.length}` : mealplans.length})</h2></div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <i className="material-icons">{expandedSection === 'mealplans' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
                <button className="plus-button" onClick={(e) => { e.stopPropagation(); setIsAddMealPlanModalOpen(true); }} title="Add meal plan"><i className="material-icons">add</i></button>
              </div>
            </div>
            {expandedSection === 'mealplans' && <MealplanManager mealplans={filteredMealplans} onEdit={startEditingMealplan} onDelete={deleteMealplan} searchTerm={mealplanSearchTerm} onSearchChange={handleMealplanSearchChange} page={mealplanPage} onPageChange={setMealplanPage} onSelect={loadMealPlan} selectedMealplan={activeMealPlan} unselectAllMealplans={unselectAllMealplans} searchInputRef={mealplanSearchRef} />}
          </div>
          <div className="collapsible-section" ref={recipesSectionRef} data-tour="recipes-section">
            <div className="section-header" onClick={() => setExpandedSection(expandedSection === 'recipes' ? null : 'recipes')}>
              <div style={{ display: 'flex', alignItems: 'center' }}><i className="material-icons" style={{ marginRight: '5px', verticalAlign: 'top' }}>restaurant_menu</i><h2>Recipes ({filteredRecipes.length !== recipes.length ? `${filteredRecipes.length} of ${recipes.length}` : recipes.length})</h2></div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <i className="material-icons">{expandedSection === 'recipes' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
                <button onClick={(e) => { e.stopPropagation(); openAddRecipeModal(); }} className="plus-button" title="Add recipe" data-tour="add-recipe-button"><i className="material-icons">add</i></button>
              </div>
            </div>
            {expandedSection === 'recipes' && (
              <div>
                {recipes.length === 0 ? (<div style={{ textAlign: 'center', padding: '1rem' }}>No recipes found.<br />Add one by using the + button</div>) : (
                  <>
                    <div className="recipe-filter-controls" style={{ display: 'flex', alignItems: 'center' }}>
                      {selectedRecipes.length > 0 && <button title="Unselect all recipes" onClick={unselectAllRecipes} className="unselect-button">Unselect</button>}
                      <div className={`search-input-container ${selectedRecipes.length > 0 ? 'collapsed' : 'expanded'}`}>
                        <input ref={recipeSearchRef} type="text" placeholder="Search Recipes" value={searchTerm} onChange={e => handleRecipeSearchChange(e.target.value)} />
                        {searchTerm && <button onClick={() => { handleRecipeSearchChange(''); recipeSearchRef.current.focus(); }} className="clear-input-button"><i className="material-icons">close</i></button>}
                      </div>
                      <button style={{ marginLeft: '1rem' }} onClick={(e) => { e.stopPropagation(); setIsFilterModalOpen(true); }} className={`icon-button ${(selectedCookbookIds.length > 0 || selectedCategoryIds.length > 0 || selectedIngredientIds.length > 0) ? 'filter-active' : ''}`} title="Filter recipes" data-tour="recipe-filter-button"><i className="material-icons">filter_list</i></button>
                      {(selectedCookbookIds.length > 0 || selectedCategoryIds.length > 0 || selectedIngredientIds.length > 0) && <button onClick={() => { setSelectedCookbookIds([]); setSelectedCategoryIds([]); setSelectedIngredientIds([]); }} className="clear-filter-button" title="Clear filter">Clear</button>}
                      <button
                        style={{ marginLeft: '0.5rem', color: isShuffled ? 'rgb(76, 175, 80)' : 'inherit' }}
                        onClick={(e) => { e.stopPropagation(); setIsShuffled(!isShuffled); }}
                        className="icon-button"
                        title={isShuffled ? "Disable shuffle" : "Shuffle recipes"}
                      >
                        <i className="material-icons" style={{ fontWeight: isShuffled ? 'bold' : 'normal' }}>shuffle</i>
                      </button>
                    </div>
                    <RecipeList recipes={filteredRecipes} selectedRecipes={selectedRecipes} onRecipeSelection={handleRecipeSelection} onEdit={startEditingRecipe} onDelete={deleteRecipe} onUnselectAll={unselectAllRecipes} cookbooks={cookbooks} categories={categories} ingredients={ingredients} page={recipePage} onPageChange={setRecipePage} searchTerm={searchTerm} selectedCookbookIds={selectedCookbookIds} selectedCategoryIds={selectedCategoryIds} selectedIngredientIds={selectedIngredientIds} onRemoveCookbook={handleRemoveCookbook} onRemoveCategory={handleRemoveCategory} onRemoveIngredient={handleRemoveIngredient} isShuffled={isShuffled} />
                  </>
                )}
              </div>
            )}
          </div>
          <div className="collapsible-section" ref={ingredientsSectionRef} data-tour="ingredients-section">
            <div className="section-header" onClick={() => setExpandedSection(expandedSection === 'ingredients' ? null : 'ingredients')}>
              <div style={{ display: 'flex', alignItems: 'center' }}><i className="material-icons" style={{ marginRight: '5px', verticalAlign: 'top' }}>kitchen</i><h2>Ingredients ({filteredIngredients.length !== ingredients.length ? `${filteredIngredients.length} of ${ingredients.length}` : ingredients.length})</h2></div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <i className="material-icons">{expandedSection === 'ingredients' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
                <button onClick={(e) => { e.stopPropagation(); openAddIngredientModal(); }} className="plus-button" title="Add ingredient" data-tour="add-ingredient-button"><i className="material-icons">add</i></button>
              </div>
            </div>
            {expandedSection === 'ingredients' && <IngredientManager ingredients={filteredIngredients} allIngredients={ingredients} onEdit={startEditingIngredient} onDelete={deleteIngredient} searchTerm={ingredientSearchTerm} onSearchChange={handleIngredientSearchChange} page={ingredientPage} onPageChange={setIngredientPage} searchInputRef={ingredientSearchRef} selectedIngredientIds={selectedIngredientIds} onIngredientSelection={handleIngredientSelection} unselectAllIngredients={unselectAllIngredients} />}
          </div>
          <div className="collapsible-section" ref={additionalItemGroupsSectionRef}>
            <div className="section-header" onClick={() => setExpandedSection(expandedSection === 'itemGroups' ? null : 'itemGroups')}>
              <div style={{ display: 'flex', alignItems: 'center' }}><i className="material-icons" style={{ marginRight: '5px', verticalAlign: 'top' }}>add_shopping_cart</i><h2>Item Groups ({filteredAdditionalItemGroups.length !== additionalItemGroups.length ? `${filteredAdditionalItemGroups.length} of ${additionalItemGroups.length}` : additionalItemGroups.length})</h2></div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <i className="material-icons">{expandedSection === 'itemGroups' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
                <button onClick={(e) => { e.stopPropagation(); openAddAdditionalItemGroupModal(); }} className="plus-button" title="Add item group"><i className="material-icons">add</i></button>
              </div>
            </div>
            {expandedSection === 'itemGroups' && <AdditionalItemGroupManager groups={filteredAdditionalItemGroups} onEdit={startEditingAdditionalItemGroup} onDelete={deleteAdditionalItemGroup} searchTerm={additionalItemGroupSearchTerm} onSearchChange={handleAdditionalItemGroupSearchChange} page={additionalItemGroupPage} onPageChange={setAdditionalItemGroupPage} onSelect={handleAdditionalItemGroupSelection} selectedGroupIds={selectedAdditionalItemGroupIds} unselectAll={unselectAllAdditionalItemGroups} searchInputRef={additionalItemGroupSearchRef} />}
          </div>
          <div className="collapsible-section" ref={categoriesSectionRef} data-tour="categories-section">
            <div className="section-header" onClick={() => setExpandedSection(expandedSection === 'categories' ? null : 'categories')}>
              <div style={{ display: 'flex', alignItems: 'center' }}><i className="material-icons" style={{ marginRight: '5px', verticalAlign: 'top' }}>widgets</i><h2>Categories ({filteredCategories.length !== categories.length ? `${filteredCategories.length} of ${categories.length}` : categories.length})</h2></div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <i className="material-icons">{expandedSection === 'categories' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
                <button onClick={(e) => { e.stopPropagation(); openAddCategoryModal(); }} className="plus-button" title="Add category" data-tour="add-category-button"><i className="material-icons">add</i></button>
              </div>
            </div>
            {expandedSection === 'categories' && <CategoryManager categories={filteredCategories} allCategories={categories} onEdit={startEditingCategory} onDelete={deleteCategory} searchTerm={categorySearchTerm} onSearchChange={handleCategorySearchChange} page={categoryPage} onPageChange={setCategoryPage} selectedCategoryIds={selectedCategoryIds} onCategorySelection={handleCategorySelection} unselectAllCategories={unselectAllCategories} searchInputRef={categorySearchRef} />}
          </div>
          <div className="collapsible-section" ref={cookbooksSectionRef} data-tour="cookbooks-section">
            <div className="section-header" onClick={() => setExpandedSection(expandedSection === 'cookbooks' ? null : 'cookbooks')}>
              <div style={{ display: 'flex', alignItems: 'center' }}><i className="material-icons" style={{ marginRight: '5px', verticalAlign: 'top' }}>menu_book</i><h2>Cookbooks ({filteredCookbooks.length !== cookbooks.length ? `${filteredCookbooks.length} of ${cookbooks.length}` : cookbooks.length})</h2></div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <i className="material-icons">{expandedSection === 'cookbooks' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
                <button onClick={(e) => { e.stopPropagation(); openAddCookbookModal(); }} className="plus-button" title="Add cookbook" data-tour="add-cookbook-button"><i className="material-icons">add</i></button>
              </div>
            </div>
            {expandedSection === 'cookbooks' && <CookbookManager cookbooks={filteredCookbooks} allCookbooks={cookbooks} onEdit={startEditingCookbook} onDelete={deleteCookbook} searchTerm={cookbookSearchTerm} onSearchChange={handleCookbookSearchChange} page={cookbookPage} onPageChange={setCookbookPage} selectedCookbookIds={selectedCookbookIds} onCookbookSelection={handleCookbookSelection} unselectAllCookbooks={unselectAllCookbooks} searchInputRef={cookbookSearchRef} />}
          </div>
        </div>
        <div className="right-panel" data-tour="meal-plan-panel">
          <MealPlan recipes={recipes} mealPlan={activeMealPlan} onRecipeRemove={removeRecipeFromMealPlan} onRecipeReorder={reorderMealPlanRecipe} onMove={moveRecipe} onSave={saveActiveMealPlan} cookbooks={cookbooks} isMealPlanDirty={isMealPlanDirty} onEdit={startEditingMealplan} onServingSizeUpdate={updateServingSize} globalServingSize={globalServingSize} onAddAdditionalItem={addAdditionalItem} onDeleteAdditionalItem={deleteAdditionalItem} onUpdateAdditionalItem={updateAdditionalItem} onCloseMealPlan={closeMealPlan} onNumberOfDaysChange={updateNumberOfDays} startTour={startTour} setIsFaqModalOpen={setIsFaqModalOpen} onStartDayChange={updateStartDay} />
          <div className="ingredients-section ingredients-print-section">
            <MealplanIngredientsManager ingredients={mealPlanIngredients} shoppingList={shoppingList} onToggleItem={handleToggleShoppingListItem} onPrintShoppingList={handlePrintShoppingList} onPrintIngredientList={handlePrintIngredientList} onSelectAll={handleSelectAllIngredients} onClearShoppingList={handleClearShoppingList} />
          </div>
          <FindRecipesByIngredientsManager recipes={recipes} />
        </div>
        <AddRecipeModal isOpen={isRecipeModalOpen} onClose={cancelEditingRecipe} onSave={(recipe, andAnother) => addRecipe(recipe, editingRecipeId, andAnother)} editingRecipe={recipes.find(r => r.id === editingRecipeId)} cookbooks={cookbooks} categories={categories} masterIngredients={ingredients} onAddCookbook={addCookbook} onAddCategory={addCategory} onAddIngredient={addIngredient} />
        <AddCookbookModal isOpen={isCookbookModalOpen} onClose={cancelEditingCookbook} onSave={addCookbook} editingCookbook={editingCookbook} />
        <AddCategoryModal isOpen={isCategoryModalOpen} onClose={cancelEditingCategory} onSave={addCategory} editingCategory={editingCategory} />
        <AddAdditionalItemGroupModal isOpen={isAdditionalItemGroupModalOpen} onClose={cancelEditingAdditionalItemGroup} onSave={addAdditionalItemGroup} editingGroup={editingAdditionalItemGroup} />
        <AddIngredientModal isOpen={isIngredientModalOpen} onClose={cancelEditingIngredient} onSave={addIngredient} editingIngredient={editingIngredient} />
        <EditMealplanModal isOpen={isEditMealplanModalOpen} onClose={cancelEditingMealplan} onSave={editMealplan} editingMealplan={editingMealplan} />
        <AddMealPlanModal isOpen={isAddMealPlanModalOpen} onClose={() => setIsAddMealPlanModalOpen(false)} onSave={addMealPlan} />
        <FilterModal isOpen={isFilterModalOpen} onClose={() => setIsFilterModalOpen(false)} cookbooks={cookbooks} categories={categories} ingredients={ingredients} appliedCookbookIds={selectedCookbookIds} appliedCategoryIds={selectedCategoryIds} appliedIngredientIds={selectedIngredientIds} onApplyFilter={handleApplyFilter} />
        <ConfirmationModal isOpen={confirmationModal.isOpen} onClose={() => setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { }, onCancel: () => { } })} onConfirm={confirmationModal.onConfirm} onCancel={confirmationModal.onCancel} message={confirmationModal.message} />
        <FaqModal isOpen={isFaqModalOpen} onClose={() => setIsFaqModalOpen(false)} activeApi={activeApi} />
        <button onClick={scrollToTop} className={`back-to-top-button ${isBackToTopVisible ? 'visible' : ''}`} title="Back to top" aria-label="Back to top">
          <i className="material-icons">arrow_upward</i>
        </button>
        {isSettingsModalOpen && <SettingsModal onClose={() => setIsSettingsModalOpen(false)} onUpdateUser={updateUser} onThemeChange={updateTheme} currentTheme={user?.theme} />}
      </div>
    </div>
  );
}

export default MainLayout;
